import { toast } from "react-toastify";
import { cn } from "utils/cn";
import { ReactNode } from "react";
import { Button } from "components/common/buttons/Button";

type position =
  | "top-right"
  | "top-center"
  | "top-left"
  | "bottom-right"
  | "bottom-center"
  | "bottom-left"
  | "top-right";
type VariantType = "info" | "success" | "error" | "default" | "warning";

const variantStyles: Record<
  VariantType,
  { container: string; icon: string; dot: string; text: string }
> = {
  success: {
    container: "bg-success/20 border-success/40",
    icon: "bg-success/30 text-success",
    dot: "bg-success",
    text: "text-success",
  },
  error: {
    container: "bg-danger/20 border-danger/40",
    icon: "bg-danger/30 text-danger",
    dot: "bg-danger",
    text: "text-danger",
  },
  info: {
    container: "bg-info/20 border-info/40",
    icon: "bg-info/30 text-info",
    dot: "bg-info",
    text: "text-info",
  },
  warning: {
    container: "bg-warning/20 border-warning/40",
    icon: "bg-warning/30 text-warning",
    dot: "bg-warning",
    text: "text-warning",
  },
  default: {
    container: "bg-info/20 border-info/40",
    icon: "bg-info/30 text-info",
    dot: "bg-info",
    text: "text-info",
  },
};

const getMessageDuration = (message: string) => {
  const minDuration = 1;
  const maxDuration = 3;
  const charsPerSecond = 30;
  const maxChars = 90;

  const messageLength = message.length;

  let duration;
  if (messageLength <= charsPerSecond) duration = minDuration;
  else if (messageLength <= maxChars) duration = maxDuration;
  else {
    duration = messageLength / charsPerSecond;
    duration = Math.max(minDuration, Math.min(maxDuration, duration));
  }

  return duration * 1000;
};

/** Warnings and errors stay up long enough to read. Clicking still dismisses them. */
export const ATTENTION_TOAST_DURATION_MS = 8000;

export const getToastDuration = (message: string, type: VariantType) =>
  type === "warning" || type === "error"
    ? ATTENTION_TOAST_DURATION_MS
    : getMessageDuration(message);

export const handleToast = (
  msg: string,
  type: VariantType = "info",
  position: position = "bottom-left",
  toastId?: string
) => {
  if (msg !== "") {
    const styles = variantStyles[type] ?? variantStyles.default;

    const content = (
      <div className="rounded-lg overflow-hidden">
        <div className="bg-background">
          <div className={cn("border rounded-lg p-4", styles.container)}>
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full",
                  styles.icon
                )}
              >
                <div className={cn("h-2 w-2 rounded-full", styles.dot)} />
              </div>
              <div className={cn("flex-1 text-xs leading-none", styles.text)}>{msg}</div>
            </div>
          </div>
        </div>
      </div>
    );

    toast(content, {
      toastId,
      position,
      autoClose: getToastDuration(msg, type),
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      className: "bg-transparent shadow-none p-0 border-0",
      icon: false,
    });
  }
};

export const handleToastWithAction = (
  msg: string,
  action: ReactNode,
  type: VariantType = "info",
  position: position = "bottom-left",
  toastId?: string,
  /** When a number, toast auto-dismisses after that many ms (e.g. timed undo). `false` keeps the toast open until dismissed. */
  autoClose: number | false = false
) => {
  if (msg !== "") {
    const styles = variantStyles[type] ?? variantStyles.default;

    const content = (
      <div className="rounded-xl overflow-hidden shadow-lg border border-border">
        <div className="bg-background">
          <div className={cn("border rounded-xl p-4", styles.container)}>
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full",
                  styles.icon
                )}
              >
                <div className={cn("h-2 w-2 rounded-full", styles.dot)} />
              </div>
              <p className={cn("min-w-0 flex-1 text-xs leading-snug", styles.text)}>{msg}</p>
              <div className="flex flex-shrink-0 items-center">{action}</div>
            </div>
          </div>
        </div>
      </div>
    );

    toast(content, {
      toastId,
      position,
      autoClose,
      hideProgressBar: true,
      closeOnClick: false, // Don't close when clicking the toast (only close button)
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      className: "bg-transparent shadow-none p-0 border-0",
      icon: false,
    });
  }
};

export default handleToast;

export const ToastActionButton = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => (
  <Button type="button" color="gray" size="xs" onClick={onClick}>
    {children}
  </Button>
);
